import { useCallback, useEffect, useRef } from "react";
import {
  AdMob,
  BannerAdPosition,
  BannerAdPluginEvents,
  BannerAdSize,
  MaxAdContentRating,
  type AdMobError,
} from "@capacitor-community/admob";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import {
  ADMOB_BANNER_AD_ID,
  ADMOB_INTERSTITIAL_AD_ID,
} from "../utils/constant";

// Play Store's Families ad-format review flagged this app for "ad walls" /
// interstitials that "interfere with normal app use" -- every one of
// TournamentManager, MatchHistoryPage, TeamLibraryManager, and JoinGame
// fires its own interstitial on mount with no awareness of any other
// interstitial that may have just shown, so quickly navigating between
// those pages (exactly what an automated policy reviewer does) could stack
// full-screen interstitials back to back. This module-level (shared across
// every useAdMob() instance, since the module itself is a singleton)
// cooldown makes "was one shown recently?" a real, app-wide question
// instead of each page only ever knowing about itself.
const MIN_INTERSTITIAL_INTERVAL_MS = 90_000;

// Publishes the native banner's current height as a CSS variable so any
// fixed/sticky bottom UI (e.g. the App Preferences save bar) can sit above
// it instead of being covered by it. 0px whenever no banner is on screen.
const setBannerHeightVar = (height: number) => {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(
    "--app-ad-banner-height",
    `${Math.max(0, Math.round(height || 0))}px`,
  );
};
let lastInterstitialShownAt = 0;

export const useAdMob = () => {
  const initialized = useRef(false);
  const listenersRegistered = useRef(false);
  const bannerLoadedListener = useRef<PluginListenerHandle | null>(null);
  const bannerFailedListener = useRef<PluginListenerHandle | null>(null);
  const bannerSizeListener = useRef<PluginListenerHandle | null>(null);

  const isNative = Capacitor.isNativePlatform();

  const initialize = useCallback(async () => {
    if (!isNative || initialized.current) return;

    try {
      // Cap ad content to general audiences so mediated/programmatic
      // inventory (e.g. dating or other mature-audience ads) is excluded.
      // Without this, AdMob defaults to serving the full content range,
      // which is what triggered the Play Store "Ad Content" rejection.
      await AdMob.initialize({
        maxAdContentRating: MaxAdContentRating.General,
        tagForChildDirectedTreatment: false,
        tagForUnderAgeOfConsent: false,
      });

      if (!listenersRegistered.current) {
        bannerLoadedListener.current = await AdMob.addListener(
          BannerAdPluginEvents.Loaded,
          () => {
            console.log("Banner loaded");
          },
        );

        bannerFailedListener.current = await AdMob.addListener(
          BannerAdPluginEvents.FailedToLoad,
          (error: AdMobError) => {
            console.log("Banner failed", JSON.stringify(error));
            setBannerHeightVar(0);
          },
        );

        bannerSizeListener.current = await AdMob.addListener(
          BannerAdPluginEvents.SizeChanged,
          (size) => {
            setBannerHeightVar(size?.height ?? 0);
          },
        );

        listenersRegistered.current = true;
      }

      initialized.current = true;
    } catch (error) {
      console.error("AdMob initialization failed", error);
    }
  }, [isNative]);

  useEffect(() => {
    return () => {
      clearHideTimer();

      if (!isNative || !listenersRegistered.current) return;

      void bannerLoadedListener.current?.remove();
      void bannerFailedListener.current?.remove();
      void bannerSizeListener.current?.remove();

      bannerLoadedListener.current = null;
      bannerFailedListener.current = null;
      bannerSizeListener.current = null;

      listenersRegistered.current = false;
    };
  }, [isNative]);

  const bannerVisible = useRef(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bannerOperation = useRef(Promise.resolve());

  const runBannerOperation = async (operation: () => Promise<void>) => {
    bannerOperation.current = bannerOperation.current
      .then(operation)
      .catch((err) => console.error("Banner operation error", err));

    return bannerOperation.current;
  };

  const clearHideTimer = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const hideBanner = useCallback(async () => {
    if (!isNative) return;

    clearHideTimer();

    await runBannerOperation(async () => {
      if (!bannerVisible.current) return;

      try {
        await AdMob.hideBanner();
        bannerVisible.current = false;
        setBannerHeightVar(0);
      } catch (error) {
        console.error("Hide banner error", error);
      }
    });
  }, [isNative]);

  const removeBanner = useCallback(async () => {
    if (!isNative) return;

    clearHideTimer();

    await runBannerOperation(async () => {
      if (!bannerVisible.current) return;

      try {
        await AdMob.removeBanner();
      } catch (error) {
        console.error("Remove banner error", error);
      } finally {
        bannerVisible.current = false;
        setBannerHeightVar(0);
      }
    });
  }, [isNative]);

  const showBanner = useCallback(
    async (durationMs = 60000) => {
      if (!isNative) return;

      clearHideTimer();

      await runBannerOperation(async () => {
        try {
          await initialize();

          // Already showing
          if (bannerVisible.current) {
            return;
          }

          if (!ADMOB_BANNER_AD_ID) {
            return;
          }

          await AdMob.showBanner({
            adId: ADMOB_BANNER_AD_ID,
            adSize: BannerAdSize.BANNER,
            position: BannerAdPosition.BOTTOM_CENTER,
          });

          bannerVisible.current = true;

          if (durationMs > 0) {
            hideTimer.current = setTimeout(() => {
              void hideBanner();
            }, durationMs);
          }
        } catch (error) {
          console.error("Banner error", error);
        }
      });
    },
    [initialize, hideBanner, isNative],
  );

  const showInterstitial = async () => {
    if (!isNative) return;

    const now = Date.now();
    if (now - lastInterstitialShownAt < MIN_INTERSTITIAL_INTERVAL_MS) {
      return;
    }

    // No interstitial unit configured for this platform: show nothing.
    if (!ADMOB_INTERSTITIAL_AD_ID) return;

    await initialize();

    try {
      await AdMob.prepareInterstitial({
        adId: ADMOB_INTERSTITIAL_AD_ID,
      });

      await AdMob.showInterstitial();
      // Only start the cooldown once an interstitial has actually shown --
      // a failed/absent prepare (e.g. no fill, no ad ID configured) should
      // not block a real one from showing on the next opportunity.
      lastInterstitialShownAt = now;
    } catch (error) {
      console.error("Interstitial error", error);
    }
  };

  return {
    initialize,
    showBanner,
    hideBanner,
    removeBanner,
    showInterstitial,
  };
};
